import React, { useState, useEffect } from 'react';

import { FileSelectModal } from '../modals/FileSelectModal';
import { getImmediateFolderObjects } from '../../utils/folderUtils';
import { CaseFolderObject,  SiteModel } from '../../models/DataAdminTypes';
import { formatDate } from '../../utils/dateUtils';
import { NewSubFolderModal } from '../modals/NewSubFolderModal';
import SortableHeader from '../../controls/sortableHeader';
import FolderBreadcrumbs from './FolderBreadcrumbs';

interface FolderContentsProps {
  caseFolder: string;
  env: string;
  folderObjects: CaseFolderObject[];
  site: SiteModel|null;
  getAccessToken: () => Promise<string>;
  onBack: () => void;
  onReload: () => void;
}

const FolderContentsHierarchical: React.FC<FolderContentsProps> =
  ({ folderObjects, site, caseFolder, env, getAccessToken, onBack, onReload }):
  React.JSX.Element => {

    const [currentLevelObjects, setCurrentLevelObjects] = useState<CaseFolderObject[]>([]);
    const [selectedSubFolder, setSelectedSubFolder] = useState<string>('');
    const [showFileSelectModal, setShowFileSelectModal] = useState(false);
    const [showNewSubfolderModal, setShowNewSubfolderModal] = useState(false);
    const [sortConfig, setSortConfig] = useState<{key: string | null, direction: string | null}>({ key: null, direction: 'asc' });

    // Update current level objects when folderObjects or selectedFolder changes
    useEffect(() => {
      const displayObjects = getImmediateFolderObjects(folderObjects, selectedSubFolder);
      setCurrentLevelObjects(displayObjects);
    }, [selectedSubFolder, folderObjects]);

    // Close modal and reload
    const handleFileUploadModalClose = (): void => {
      setShowFileSelectModal(false);
      onReload();
    };

    // Handle creating a new subfolder
    const showCreateSubfolder = async (): Promise<void> => {
      setShowNewSubfolderModal(true);
    };

    // Handle sorting
    const handleSort = (key: string): void => {
      let direction = 'asc';
      if (sortConfig.key === key && sortConfig.direction === 'asc') {
        direction = 'desc';
      }
      const config = { key: key, direction: direction };
      setSortConfig(config);
    };

    const fileObjectDisplayName = (obj: CaseFolderObject): string => {
      return obj.filePathSansCaseFolder.split('/').pop() || '';
    };

    // Sort the data based on current sort config
    const sortedObjects = [...currentLevelObjects]
      .filter(obj => !(obj.isFolder && !obj.isSubFolder)) // filter out "empty" folders
      .sort((a, b) => {
        if (!sortConfig.key) return 0;

        const aValue = a[sortConfig.key as keyof CaseFolderObject];
        const bValue = b[sortConfig.key as keyof CaseFolderObject];

        if (aValue && bValue) {
          if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          } else {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
        }
        return 0;
      });

    if (!site || !caseFolder) {
      return <div>Select a Site to view Case Folders</div>;
    }

    return (
      <>
        <FolderBreadcrumbs
          caseFolder={caseFolder}
          selectedSubFolder={selectedSubFolder}
          onSelectCaseFolder={onBack}
          onSelectSubFolder={setSelectedSubFolder}
        />

        <div className="mb-4 flex justify-between items-center px-4 py-2 bg-gray-100 border border-gray-200 rounded">
          <span>Total Folder Objects: {folderObjects.filter(f => !f.isFolder).length}</span>

          <button
            onClick={() => {setShowFileSelectModal(true);}}
            className={'action-primary'}
          >
            <span>Upload Files</span>
          </button>
        </div>

        <div data-testid="folder-contents-container" className="h-screen min-h-40 overflow-auto">

          <table data-testid="folder-contents-table" className="w-full table-scrollable">
            <thead>
              <tr>
                <SortableHeader
                  label="File Name"
                  sortKey="filePathSansCaseFolder"
                  onSort={handleSort}
                  sortConfig={sortConfig}
                />
                <SortableHeader
                  label="Size"
                  sortKey="size"
                  onSort={handleSort}
                  sortConfig={sortConfig}
                />
                <SortableHeader
                  label="Last Modified"
                  sortKey="lastModified"
                  onSort={handleSort}
                  sortConfig={sortConfig}
                />
              </tr>
            </thead>
            <tbody>
              <tr
                data-testid="new-subfolder-row"
                className="table-scrollable-row cursor-pointer hover:bg-gray-50"
                onClick={showCreateSubfolder}
                title="Create new folder"
              >
                <td className="flex items-center text-gray-700 text-sm py-2 px-1">
                  <span className="mr-2 text-green-600 text-xl font-bold">+</span>
                  Create Subfolder
                </td>
                <td className="text-gray-500">--</td>
                <td className="text-gray-500">--</td>
              </tr>
              {sortedObjects.filter(f => f.isSubFolder).map(folderObject => {
                return (
                  <tr
                    className="table-scrollable-row"
                    key={folderObject.objectKey}
                    onClick={() => setSelectedSubFolder(folderObject.filePathSansCaseFolder)}
                    title="Open folder"
                  >
                    <td>
                      <span className="mr-2" role="img" aria-label="folder">📁</span>
                      {fileObjectDisplayName(folderObject)}
                    </td>
                    <td>--</td>
                    <td>{formatDate(folderObject.lastModified)}</td>
                  </tr>
                );
              })}

              {sortedObjects.filter(f => !f.isSubFolder).map(folderObject => {
                return (
                  <tr
                    className="table-scrollable-row"
                    key={folderObject.objectKey}
                  >
                    <td>
                      {fileObjectDisplayName(folderObject)}
                    </td>
                    <td>{folderObject.sizeDesc}</td>
                    <td>{formatDate(folderObject.lastModified)}</td>
                  </tr>
                );
              })}

            </tbody>
          </table>

        </div>

        {showFileSelectModal && (
          <FileSelectModal
            getAccessToken={getAccessToken}
            caseFolder={caseFolder}
            siteCode={site.siteCode}
            subFolder={selectedSubFolder}
            env={env}
            onClose={handleFileUploadModalClose}
          />
        )}

        {showNewSubfolderModal && (
          <NewSubFolderModal
            getAccessToken={getAccessToken}
            caseFolder={caseFolder}
            currentSubFolder={selectedSubFolder}
            site={site}
            env={env}
            onClose={() => { setShowNewSubfolderModal(false); onReload(); }}
          />
        )}
      </>
    );
  };
export default FolderContentsHierarchical;
